import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CircularProgress } from "@mui/material";
import clinicService from "../../services/clinic.service";
import "../reputation/reputation.css";
import "./welcome.css";
import "./appointments.css";

const formatDate = (dateString) => {
  if (!dateString) return "—";
  const options = { weekday: "long", year: "numeric", month: "long", day: "numeric" };
  return new Date(dateString).toLocaleDateString(undefined, options);
};

const formatTime = (timeString) => {
  if (!timeString) return "—";
  const options = { hour: "numeric", minute: "numeric", hour12: true };
  return new Date(`2000-01-01T${timeString}`).toLocaleTimeString(undefined, options);
};

function Appointments() {
  const { clinicId, appointmentId } = useParams();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [appointment, setAppointment] = useState(null);
  const [calendarSaved, setCalendarSaved] = useState(false);

  useEffect(() => {
    if (!clinicId || !appointmentId) {
      setLoading(false);
      setError("This confirmation link is incomplete. Please use the link from your reminder email.");
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError("");

    clinicService
      .confirmAppointment(clinicId, appointmentId)
      .then((res) => {
        if (!cancelled) {
          setAppointment(res.data || null);
        }
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.log("Failed to confirm appointment", err);
        if (!cancelled) {
          const message =
            err?.response?.data?.message ||
            "We could not confirm your appointment. The link may be invalid or the appointment may have already passed.";
          setError(message);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [clinicId, appointmentId]);

  const saveToCalendar = () => {
    setCalendarSaved(true);
    setTimeout(() => setCalendarSaved(false), 3000);
  };

  if (loading) {
    return (
      <div className="welcome-stack">
        <section className="rep-panel welcome-loading">
          <CircularProgress size={28} />
          <p className="rep-copy" style={{ margin: 0 }}>
            Confirming your appointment…
          </p>
        </section>
      </div>
    );
  }

  if (error || !appointment) {
    return (
      <div className="welcome-stack">
        <section className="rep-panel">
          <div className="rep-done">
            <div className="rep-done-mark appt-done-mark--cancel" aria-hidden="true">
              <i className="bi bi-exclamation-circle" />
            </div>
            <h2 className="rep-title">Unable to confirm</h2>
            <p className="rep-copy" style={{ marginBottom: 0 }}>
              {error || "We could not confirm your appointment. Please contact your clinic for help."}
            </p>
          </div>
        </section>
      </div>
    );
  }

  const patient = appointment.patient || {};

  return (
    <div className="welcome-stack">
      <section className="rep-panel">
        <div className="rep-done">
          <div className="rep-done-mark" aria-hidden="true">
            <i className="bi bi-check-lg" />
          </div>
          <h2 className="rep-title">Appointment confirmed</h2>
          <p className="rep-copy" style={{ marginBottom: 0 }}>
            Your appointment has been confirmed. See the details below.
          </p>
        </div>
      </section>

      <section className="rep-panel">
        <h3 className="welcome-section-title">Appointment details</h3>
        <p className="welcome-section-copy">Patient appointment</p>
        <div className="appt-details">
          <div className="appt-row">
            <span className="appt-label">Date</span>
            <span className="appt-value">{formatDate(appointment.dateAppointment)}</span>
          </div>
          <div className="appt-row">
            <span className="appt-label">Time</span>
            <span className="appt-value">{formatTime(appointment.startTime)}</span>
          </div>
        </div>
        <div className="appt-actions">
          <button
            type="button"
            className="rep-btn rep-btn--primary"
            onClick={saveToCalendar}
            disabled={calendarSaved}
          >
            {calendarSaved ? "Saved to calendar" : "Save to calendar"}
          </button>
        </div>
      </section>

      <section className="rep-panel">
        <h3 className="welcome-section-title">Patient</h3>
        <p className="welcome-section-copy">Who this appointment is for.</p>
        <div className="appt-patient-list">
          <div className="appt-patient-card">
            <p className="appt-patient-name">
              {patient.firstName} {patient.lastName}
            </p>
            {patient.preferredName ? (
              <p className="appt-patient-preferred">
                Preferred name: {patient.preferredName}
              </p>
            ) : null}
          </div>
        </div>
      </section>
    </div>
  );
}

export default Appointments;
